<script setup>
import { useShopify } from '@/stores/shopify'
import { ref, computed } from 'vue'
import InputSearch from '@/components/atoms/form/InputSearch.vue';
import RoomCard from '@/components/RoomCard.vue';
import { useRoute } from 'vue-router';
import Splide from '@splidejs/splide'

const MIN_INFLUENCERS_SEARCH = 4;

const emit = defineEmits(['close'])

const shopify = useShopify()

const influencers = ref([])
const search = ref('');
const lobby = ref();
const route = useRoute();
const highlightSliderRef = ref();

const props = defineProps({
  showSearch: {
    type: Boolean,
    default: true,
  },
  showShare: {
    type: Boolean,
    default: true,
  },
  showCurrent: {
    type: Boolean,
    default: false,
  },
  inSidesheet: {
    type: Boolean,
    default: false,
  }
})

const setupSlider = () => {
  if (highlightSliderRef.value == null) {
    window.setTimeout(() => {
      setupSlider();
    }, 100)
    return;
  }

  const slider = new Splide(highlightSliderRef.value, {
    type: 'loop',
    padding: '39px',
    gap: '16px',
    flickPower: 300,
    flickMaxPages: 1,
    arrows: false,
    pagination: false,
    mediaQuery: 'min',
    breakpoints: {
      510: {
        perPage: 2,
      },
      1000: {
        perPage: 3,
      },
    },
  })

  slider.mount();
}

shopify
  .fetchInfluencers(false, 128, 128)
  .then((response) => {
      influencers.value = response;
      setupSlider();
  })

const highlights = computed(() => {
  return influencers.value.filter((influencer) => influencer.node.highlight.value === 'true');
})

const onClick = () => {
  if (props.inSidesheet) {
    emit('close');
  }
}
</script>

<template>
  <div class="lobby" ref="lobby" :class="{ 'lobby--sidesheet': inSidesheet }">
    <div v-if="highlights.length > 0" ref="highlightSliderRef" class="splide">
      <div class="splide__track">
        <ul class="splide__list">
          <li class="splide__slide" v-for="influencer in highlights" :key="influencer">
            <RoomCard
              :roomHandle="influencer.node.handle"
              :open="influencer.node.open.value === 'true'"
              :bgImage="influencer.node.backdrop_image.reference.previewImage.url"
              :bgColor="influencer.node.color?.value"
              :avatar="influencer.node.image.url"
              :title="influencer.node.title"
              :current="showCurrent && route.params.room_id === influencer.node.handle"
              type="highlight"
              @click="onClick"
            />
          </li>
        </ul>
      </div>
    </div>

    <h1 class="welcome">
      Entdecke eine neue wyrld
    </h1>

    <div class="search-container">
      <div class="search__text">
        Tauche ein in ein einzigartiges Erlebnis, bei dem deine Lieblings-Influencer ihre Räume für dich öffnen.
      </div>

      <div class="search__middle">&nbsp;</div>

      <div v-if="showSearch && influencers.length >= MIN_INFLUENCERS_SEARCH " class="search">
        <InputSearch v-model="search" label="Suchen" class="rooms__search" />
      </div>
    </div>

    <div class="rooms" ref="roomContainer">

      <RoomCard
        v-for="influencer in influencers"
        :key="influencer.node.id"
        :roomHandle="influencer.node.handle"
        :open="influencer.node.open.value === 'true'"
        :bgImage="influencer.node.backdrop_image.reference.previewImage.url"
        :bgColor="influencer.node.color?.value"
        :avatar="influencer.node.image.url"
        :title="influencer.node.title"
        :current="showCurrent && route.params.room_id === influencer.node.handle"
        @click="onClick"
        v-show="influencer.node.title.toLowerCase().includes(search.toLowerCase())"
      >
      </RoomCard>
    </div>
  </div>

</template>

<style scoped lang="scss">
@use '@styles/sass_variables.scss' as *;

.lobby {
  padding-top: var(--header-height);

  width: 100%;

  display: flex;
  flex-direction: column;

  &--sidesheet {
    // side sheet is screensize minus header height
    height: calc(100dvh - 56px);
    padding-top: 0;
    // padding: 0 10px;
    .search-container {
      padding: 0 16px;
    }

    .rooms {
      padding: 16px;
    }

    .welcome {
      display: none;
    }

    .splide {
      display: none;
    }
  }
}

.splide {
  min-height: 216px;
  height: 216px;

  @media screen and (min-width: $breakpoint-medium) {
    display: none;
  }
}

.welcome {
  margin: 20px 0 0 0;
  color: rgba(222, 239, 246, 1);
  text-align: center;
  font-family: "Open Sans";
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 27.631px;

  @media screen and (min-width: $breakpoint-medium) {
    margin: 20px 40px 24px 43px;
    font-size: 60px;
    line-height: normal;
    text-align: left;
  }

  @media screen and (min-width: $breakpoint-large) {
    margin-top: 40px;
  }
}

.search-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 25px;
  padding: 20px 24px 20px 24px;
  margin: 0;
  position: sticky;
  top: 60px;
  background-color: var(--clr-brand-500);
  z-index: 99;

  .search__middle {
    display: none;
  }

  @media screen and (min-width: $breakpoint-medium) {
    padding: 0 48px 60px 48px;
    grid-template-columns: 1fr 1fr;
    gap: 42px;
    top: 80px;

    .search {
      display: flex;
      flex-direction: column;
      align-items: end;
      justify-content: flex-end;
    }
  }

  @media screen and (min-width: $breakpoint-large) {
    .search__middle {
      display: block;
    }

    grid-template-columns: 1fr 1fr 1fr;
  }

  .search__text {
    display: none;
    color: var(--clr-white);
    color: rgba(222, 239, 246, 0.80);
    font-family: "Open Sans";
    font-size: 20px;
    font-style: normal;
    font-weight: 300;
    line-height: 160%;

    @media (min-width: $breakpoint-medium) {
      display: block;
    }
  }
}

.rooms {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: max-content;
  background: var(--clr-brand-500);

  gap: 20px 25px;
  // max-height: calc(100% - 146px);
  // height: 100%;
  padding: 0 24px;
  padding-bottom: 25px;

  @media (min-width: $breakpoint-medium) {
    padding: 0 48px;
    padding-bottom: 48px;
    gap: 42px;
  }

  &:not(.fixed-size) {
    @media (min-width: $breakpoint-large) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &__search {
    @media (min-width: $breakpoint-medium) {
      height: 60px;
      border-radius: 24px;
    }
  }
}
</style>
